import { Typography } from "@material-tailwind/react";
import { Table } from "../../../components/table/Table";
import { OverflowTooltip } from "../../../components/table/TableCellTooltip";
import { t } from "../../../i18n";
import { toast } from "react-hot-toast";
import { api } from "../../../services/api/api";
import PartForm, { PartFormValidator } from "../../../components/data/part/PartForm";
import EditCreateDrawer from "../../../components/edit-create-drawer/EditCreateDrawer";
import { validator } from "../../../services/form-validator/form-validator";
import { hasExternalId, isOdooConnected, saveExternalId } from "../../../services/odoo/odoo";
import { useUserRoles } from "../../../hooks/useUserRoles";
import { showApiErrorToast } from "../../../services/api/error";
import { isPolandRealm } from "../../../services/utils/utils";
import { getPartTypeI18nKey } from "../../../services/utils/partTypeUtils";

export default function PartsTable({
  parts,
  selectedHead,
  orderDesc,
  onHeadClick,
  onPartUpdated
}: {
  parts: EasyTech.Part[],
  selectedHead?: string,
  orderDesc?: boolean,
  onHeadClick?: (head: string, orderDesc: boolean) => void,
  onPartUpdated?: () => void
}) {
  const isNotOdooConnected = !isOdooConnected();
  const showBarcode = isPolandRealm();
  const { isSuperAdmin } = useUserRoles();

  /*
   * Sur un realm relié à Odoo le catalogue est maître dans Odoo : personne n'édite une pièce ici,
   * sauf un super admin, à qui le formulaire ouvre le seul champ qu'EasyTech possède vraiment sur
   * ces realms — l'identifiant Odoo.
   */
  const canEditParts = isNotOdooConnected || isSuperAdmin;

  const TABLE_HEAD = [
    { label: t("common:name"), orderKey: "label", width: "30%" },
    { label: t("piece:ref"), orderKey: "ref", width: "20%" },
    { label: t("piece:category"), orderKey: "category.label", width: "10%" },
    { label: t("common:type"), orderKey: "type", width: "20%" },
    { label: t("common:sellingPrice"), orderKey: "price", width: "10%" },
    { label: t("piece:unity"), orderKey: "unity", width: "10%" },
    ...(showBarcode ? [{ label: t("piece:barcode"), orderKey: "barcode", width: "10%" }] : []),
    /*
     * L'identifiant Odoo n'existe que sur un realm relié à Odoo. Non triable : le tri part à l'API
     * sur `search`, et rien ne dit qu'elle accepte ce champ — un en-tête cliquable qui échoue serait
     * pire que pas d'en-tête cliquable.
     */
    ...(!isNotOdooConnected
      ? [{ label: t("piece:odoo:externalId:label"), orderKey: null, width: "10%" }]
      : []),
    ...(canEditParts ? [{ label: t("common:actions"), orderKey: null, width: "5%" }] : [])
  ];

  /**
   * handlePartEdit: enregistre les modifications du tiroir.
   *
   * Deux écritures, dans cet ordre voulu : l'identifiant Odoo passe par son propre verbe, réservé
   * au super admin, et part en premier. Un identifiant déjà pris répond 409, et échouer avant le
   * PUT laisse l'article entièrement intact — le tiroir reste ouvert sur la saisie. `saveExternalId`
   * n'appelle rien quand la valeur n'a pas bougé, c'est-à-dire pour toute édition ordinaire.
   *
   * @param stored - la pièce telle qu'affichée, avant édition
   * @param part - la pièce telle que saisie
   */
  const handlePartEdit = async (stored: EasyTech.Part, part: EasyTech.Part): Promise<void> => {
    const partToSave = {
      ...part,
      price: typeof part.price === "string" ? parseFloat(part.price) : part.price
    };

    if (!partToSave.category?.id) {
      delete partToSave.category;
    }

    if (!validator.isFormValid(partToSave, PartFormValidator)) {
      const fieldNames = {
        label: t("common:label"),
        ref: t("common:ref:label"),
        category: t("common:category"),
        price: t("common:price"),
        unity: t("common:unity"),
        serialNumber: t("common:serialNumber"),
        releaseYear: t("common:releaseYear"),
        type: t("common:type"),
        brand: t("common:brand")
      };

      toast.error(validator.getFormErrorMessage(partToSave, PartFormValidator, fieldNames, t));
      throw new Error("invalid part form");
    }

    try {
      await saveExternalId("parts", partToSave.id, stored.externalId, partToSave.externalId);
    } catch (err) {
      console.error(err);
      showApiErrorToast(err, { fallbackKey: "piece:odoo:externalId:updateError" });
      throw err;
    }

    try {
      await api.put(`users/parts/${partToSave.id}`, partToSave);
    } catch (err) {
      console.error(err);
      showApiErrorToast(err);
      throw err;
    }

    toast.success(t("workorder:parts:success:editPart"));
    onPartUpdated?.();
  };

  return (
    <Table
      tableHead={TABLE_HEAD}
      emptyTable={!parts || parts.length === 0 ? "No Parts" : false}
      selectedHead={selectedHead}
      orderDesc={orderDesc}
      onHeadClick={onHeadClick}
      enableRowLinks
    >
      {parts.map((part, index) => {
        const isLast = index === parts.length - 1;
        const rowClass = isLast ? "p-2" : "p-2 border-b border-blue-gray-50";

        return (
          <tr
            key={`${part.id}_${index}`}
            className={rowClass}
            data-href={`/parts/${part.id}`}
          >
            <td className={rowClass}>
              <Typography variant="small" color="blue-gray" className="et-cell-wrap">
                {part.label || " "}
              </Typography>
            </td>

            <td className={rowClass}>
              <Typography variant="small" color="blue-gray" className="et-cell-wrap">
                {part.ref || " "}
              </Typography>
            </td>

            <td className={rowClass}>
              <OverflowTooltip content={part.category?.label || ""} className="et-cell-truncate">
                <Typography variant="small" color="blue-gray">
                  {part.category?.label || " "}
                </Typography>
              </OverflowTooltip>
            </td>

            <td className={rowClass}>
              {(() => {
                const typeLabel = part.type
                  ? getPartTypeI18nKey(part.type)
                    ? t(getPartTypeI18nKey(part.type)!)
                    : part.type
                  : " ";
                return (
                  <OverflowTooltip content={typeLabel} className="et-cell-truncate">
                    <Typography variant="small" color="blue-gray">
                      {typeLabel}
                    </Typography>
                  </OverflowTooltip>
                );
              })()}
            </td>

            <td className={rowClass}>
              {(() => {
                const priceLabel = typeof part.price === "number" ? part.price.toFixed(2) : " ";
                return (
                  <OverflowTooltip content={priceLabel} className="et-cell-truncate">
                    <Typography variant="small" color="blue-gray">
                      {priceLabel}
                    </Typography>
                  </OverflowTooltip>
                );
              })()}
            </td>

            <td className={rowClass}>
              <OverflowTooltip content={part.unity || ""} className="et-cell-truncate">
                <Typography variant="small" color="blue-gray">
                  {part.unity || " "}
                </Typography>
              </OverflowTooltip>
            </td>

            {showBarcode && (
              <td className={rowClass}>
                <Typography variant="small" color="blue-gray">
                  {part.barcode || " "}
                </Typography>
              </td>
            )}

            {!isNotOdooConnected && (
              <td className={rowClass}>
                <Typography variant="small" color="blue-gray">
                  {hasExternalId(part.externalId)
                    ? part.externalId
                    : t("piece:odoo:externalId:notSynced")}
                </Typography>
              </td>
            )}

            {canEditParts && (
              <td className={rowClass} data-no-link>
                <EditCreateDrawer<EasyTech.Part>
                  entityName="Part"
                  data={part}
                  FormComponent={PartForm}
                  onSubmit={(edited) => handlePartEdit(part, edited)}
                  isNotCreatable
                />
              </td>
            )}
          </tr>
        );
      })}
    </Table>
  );
}
